import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

const ACTIVE_POSTINGS = [
  { id: 1, title: "Warehouse Loaders x3",  category: "Labor",        pay: "$22/hr", applicants: 14, urgency: "Urgent", posted: "2h ago"    },
  { id: 2, title: "Event Setup Crew x5",   category: "Events",       pay: "$20/hr", applicants: 8,  urgency: "Today",  posted: "5h ago"    },
  { id: 3, title: "Office Cleaners",        category: "Cleaning",     pay: "$18/hr", applicants: 6,  urgency: "Flex",   posted: "1 day ago" },
  { id: 4, title: "Delivery Driver (CDL)",  category: "Driving",      pay: "$28/hr", applicants: 11, urgency: "Urgent", posted: "3h ago"    },
];

const HIRED_WORKERS = [
  { id: 1, name: "Marcus Rivera",  role: "Loader",        rating: 5, jobs: 3, avatar: "https://public.readdy.ai/ai/img_res/ca7b5709e8753c3cb6e80a96e488fdbc.jpg" },
  { id: 2, name: "Tyra Johnson",   role: "Cleaner",       rating: 5, jobs: 2, avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20black%20woman%20smiling%20friendly%20clean%20background%20studio%20lighting&width=80&height=80&seq=hw2&orientation=squarish" },
  { id: 3, name: "Carlos Mendez",  role: "Event Crew",    rating: 4, jobs: 4, avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20latin%20man%20friendly%20clean%20office%20background%20studio%20lighting&width=80&height=80&seq=hw3&orientation=squarish" },
  { id: 4, name: "Aisha Williams", role: "Warehouse",     rating: 5, jobs: 2, avatar: "https://readdy.ai/api/search-image?query=professional%20headshot%20young%20african%20american%20woman%20smiling%20studio%20background%20natural%20light&width=80&height=80&seq=hw4&orientation=squarish" },
];

const URGENCY_COLORS: Record<string, string> = {
  Urgent: "bg-red-100 text-red-700",
  Today:  "bg-orange-100 text-orange-700",
  Flex:   "bg-gray-100 text-gray-600",
};

export default function EmployerProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"postings" | "hired" | "about">("postings");

  if (!user || user.type !== "employer") {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <i className="ri-lock-line text-4xl text-gray-400 mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Employer account required</h2>
            <p className="text-sm text-gray-500 mb-5">Please sign in as an Employer to view this page.</p>
            <Link to="/login" className="px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />

      {/* Profile Hero */}
      <section className="pt-20">
        <div className="h-44 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20abstract%20dark%20charcoal%20geometric%20professional%20banner%20background%20sleek%20corporate%20design%20clean%20minimalist%20texture%20high%20resolution&width=1400&height=200&seq=embanner1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/60 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-14 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-5 pb-6 border-b border-gray-100">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-white bg-white flex items-center justify-center">
              <img
                src={user.avatar}
                alt={user.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-extrabold text-gray-900">BuildFast Co.</h1>
                <span className="text-xs font-bold bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
                  Employer
                </span>
                <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">
                  Verified Business
                </span>
              </div>
              <p className="text-sm text-gray-500">Construction &amp; Labor · Events · Logistics</p>
              <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-map-pin-line" />Manhattan, NY</span>
                <span className="flex items-center gap-1"><i className="ri-user-line" />{user.name}</span>
                <span className="flex items-center gap-1"><i className="ri-briefcase-line" />42 jobs posted</span>
                <span className="flex items-center gap-1"><i className="ri-star-fill text-amber-400" />4.8 employer rating</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/hire"
                className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line mr-1" />Post a Job
              </Link>
              <Link
                to="/dashboard/employer"
                className="px-4 py-2 rounded-lg text-xs font-bold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-dashboard-line mr-1" />Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-xs font-bold border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-line mr-1" />Sign Out
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-7">

          {/* Left: Stats */}
          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-4">Hiring Stats</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Jobs Posted",    value: "42",     icon: "ri-file-list-3-line", color: "text-sky-600",    bg: "bg-sky-50"    },
                  { label: "Workers Hired",  value: "118",    icon: "ri-team-line",        color: "text-emerald-600", bg: "bg-emerald-50" },
                  { label: "Active Jobs",    value: "4",      icon: "ri-briefcase-4-line", color: "text-orange-600", bg: "bg-orange-50" },
                  { label: "Total Spend",    value: "$9,840", icon: "ri-money-dollar-circle-line", color: "text-green-700", bg: "bg-green-50" },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${s.bg} mb-1.5`}>
                      <i className={`${s.icon} ${s.color} text-xs`} />
                    </div>
                    <div className="text-base font-extrabold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-4">Company Details</h3>
              <div className="flex flex-col gap-3 text-xs">
                {[
                  { icon: "ri-building-2-line",      label: "Industry",   value: "Construction & Labor"   },
                  { icon: "ri-group-line",            label: "Team Size",  value: "11–50 employees"         },
                  { icon: "ri-calendar-line",         label: "Founded",    value: "2018"                    },
                  { icon: "ri-global-line",           label: "Website",    value: "buildfast.co"            },
                  { icon: "ri-map-pin-line",          label: "Location",   value: "Manhattan, NY 10001"     },
                ].map(r => (
                  <div key={r.label} className="flex items-start gap-2">
                    <i className={`${r.icon} text-gray-400 mt-0.5`} />
                    <div className="flex-1">
                      <span className="text-gray-500">{r.label}: </span>
                      <span className="font-semibold text-gray-800">{r.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Quick Actions</h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: "Post a New Job",      to: "/hire",                icon: "ri-add-circle-line"      },
                  { label: "View All Applicants", to: "/dashboard/employer",  icon: "ri-user-search-line"     },
                  { label: "Upgrade to Pro",      to: "/pricing",             icon: "ri-vip-crown-line"       },
                ].map(a => (
                  <Link key={a.label} to={a.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className={`${a.icon} text-gray-400`} />{a.label}
                    <i className="ri-arrow-right-s-line ml-auto text-gray-300" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Tabs */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
              {(["postings", "hired", "about"] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab === "postings" ? "Active Postings" : tab === "hired" ? "Hired Workers" : "About"}
                </button>
              ))}
            </div>

            {/* Active Postings */}
            {activeTab === "postings" && (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">4 Active Job Posts</h3>
                  <Link to="/hire" className="text-xs font-bold text-emerald-600 hover:underline whitespace-nowrap">
                    <i className="ri-add-line mr-1" />Post New
                  </Link>
                </div>
                <div className="divide-y divide-gray-50">
                  {ACTIVE_POSTINGS.map(job => (
                    <div key={job.id} className="px-5 py-4 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-gray-900">{job.title}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${URGENCY_COLORS[job.urgency]}`}>
                            {job.urgency}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">{job.category} · {job.pay} · Posted {job.posted}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-extrabold text-gray-900">{job.applicants}</div>
                        <div className="text-xs text-gray-400">applicants</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hired Workers */}
            {activeTab === "hired" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h3 className="font-bold text-gray-900 text-sm mb-4">Workers You've Hired</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {HIRED_WORKERS.map(w => (
                    <div key={w.id} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                      <img src={w.avatar} alt={w.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">{w.name}</div>
                        <div className="text-xs text-gray-500">{w.role} · {w.jobs} jobs</div>
                        <div className="flex gap-0.5 mt-1">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i key={i} className={`${i < w.rating ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`} />
                          ))}
                        </div>
                      </div>
                      <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors flex-shrink-0">
                        <i className="ri-message-2-line text-gray-600 text-sm" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* About Tab */}
            {activeTab === "about" && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">About BuildFast Co.</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    BuildFast Co. is a Manhattan-based labor and construction services firm that specializes in
                    fast-turnaround staffing for warehouse operations, event production, commercial cleaning, and
                    CDL-required logistics. We work with Gig <span className="italic">n&apos;</span> Cash to fill open roles quickly — often same-day — across
                    NYC's five boroughs.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-3">Categories We Hire For</h3>
                  <div className="flex flex-wrap gap-2">
                    {["Construction", "Warehouse & Logistics", "Event Setup", "Commercial Cleaning", "CDL Driving", "General Labor", "Hospitality"].map(tag => (
                      <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 font-medium">{tag}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-3">Why Workers Love Us</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { icon: "ri-money-dollar-circle-line", text: "Same-day or next-day pay" },
                      { icon: "ri-time-line",                text: "Flexible hours & shifts"  },
                      { icon: "ri-shield-check-line",        text: "Safe work environments"  },
                      { icon: "ri-user-star-line",           text: "5-star employer rating"  },
                    ].map(p => (
                      <div key={p.text} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className={`${p.icon} text-emerald-600 flex-shrink-0`} />{p.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
